import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Building2, TrendingUp, PlusCircle, MapPin, Users, Phone } from 'lucide-react';
import axios from 'axios';
import NewsletterSignup from '../components/NewsletterSignup';

/**
 * HomeTasteSkillV3 - Marktplatz + Persönliche Vermittlung
 * 
 * Strategische Positionierung:
 * - Digitaler Marktplatz für GmbH-Kauf/Verkauf
 * - Persönliche Unternehmensvermittlung und Beratung
 * 
 * Änderungen gegenüber V2:
 * - Neue Headline: "GmbH kaufen. Unternehmen verkaufen. Zukunft gestalten."
 * - Zwei gleichwertige CTAs: Marktplatz + Beratung
 * - Echte GmbH-Daten aus dem Backend
 * - Kein "Empfohlen"-Badge
 * - Neue Sektion: Persönliche Unternehmensvermittlung
 * - Alle 3 Services gleichwertig dargestellt
 */

const HomeTasteSkillV3 = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Echte Listings aus dem Backend laden
  useEffect(() => {
    const loadListings = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/listings`);
        const data = Array.isArray(res.data) ? res.data : [];
        // Nur verfügbare Listings, max 3
        const available = data.filter(l => l.status === 'Verfügbar').slice(0, 3);
        setListings(available);
      } catch (error) {
        console.error('Failed to load listings:', error);
      } finally {
        setLoading(false);
      }
    };
    loadListings();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-24 pb-14 lg:pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#F8FAFA] to-white">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            
            {/* Left: Content */}
            <div className="lg:col-span-6 pt-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E9F7F6] rounded-full mb-5">
                <div className="w-1.5 h-1.5 bg-[#009E9A] rounded-full"></div>
                <span className="text-xs font-semibold text-[#009E9A] uppercase tracking-wider">
                  Unternehmensvermittlung
                </span>
              </div>
              
              <h1 className="text-[32px] md:text-[38px] lg:text-[42px] font-bold text-[#0B1930] mb-4 leading-[1.12] tracking-[-0.02em]">
                GmbH kaufen.<br />
                Unternehmen verkaufen.<br />
                Zukunft gestalten.
              </h1>
              
              <p className="text-base md:text-lg text-gray-600 mb-6 leading-relaxed max-w-md">
                Digitaler Marktplatz und persönliche Begleitung für GmbH-Transaktionen in Deutschland.
              </p>

              {/* Zwei gleichwertige CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <Link to="/marktplatz">
                  <Button className="w-full sm:w-auto bg-[#0B1930] hover:bg-[#162844] text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors">
                    GmbH-Angebote entdecken
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/kontakt">
                  <Button variant="outline" className="w-full sm:w-auto border-[#0B1930] text-[#0B1930] hover:bg-[#E9F7F6] text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors">
                    <Phone className="mr-2 w-4 h-4" />
                    Persönliche Beratung
                  </Button>
                </Link>
              </div>

              {/* Kompakte Trust-Indikatoren */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span>Diskrete Abwicklung</span>
                <span className="text-gray-300">|</span>
                <span>Persönliche Betreuung</span>
                <span className="text-gray-300 hidden sm:inline">|</span>
                <span className="hidden sm:inline">Strukturierte Prozesse</span>
              </div>
            </div>

            {/* Right: Live Marketplace Preview */}
            <div className="lg:col-span-6">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="px-5 py-3 border-b border-gray-100 bg-[#F8FAFA]">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#0B1930] uppercase tracking-wider">Aktuelle GmbH-Angebote</span>
                    <span className="text-xs text-gray-400">{loading ? '...' : `${listings.length} verfügbar`}</span>
                  </div>
                </div>
                
                {/* Listings */}
                <div className="divide-y divide-gray-100">
                  {loading ? (
                    <div className="px-5 py-8 text-center text-sm text-gray-400">
                      Angebote werden geladen...
                    </div>
                  ) : listings.length > 0 ? (
                    listings.map((listing, idx) => (
                      <div key={listing.id || idx} className="px-5 py-4 hover:bg-[#F8FAFA]/50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="inline-flex px-2 py-0.5 text-[10px] font-semibold rounded bg-[#E9F7F6] text-[#009E9A]">
                                Verfügbar
                              </span>
                              <span className="text-[10px] text-gray-400">{listing.industry}</span>
                            </div>
                            <div className="text-sm font-semibold text-[#0B1930] mb-1 truncate">
                              {listing.title}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {listing.location}
                              </span>
                              <span>Gründung {listing.founded_year}</span>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-[10px] text-gray-400 mb-0.5">Kaufpreis</div>
                            <div className="text-sm font-bold text-[#0B1930]">
                              {listing.price?.toLocaleString('de-DE')} €
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-5 py-8 text-center text-sm text-gray-400">
                      Derzeit keine Angebote verfügbar
                    </div>
                  )}
                </div>
                
                {/* Footer */}
                <div className="px-5 py-3 border-t border-gray-100 bg-[#F8FAFA]">
                  <Link to="/marktplatz" className="flex items-center justify-center gap-2 text-sm font-medium text-[#009E9A] hover:text-[#008580] transition-colors">
                    Alle Angebote ansehen
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Alle gleichwertig */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-xl mb-10">
            <h2 className="text-2xl md:text-[28px] font-bold text-[#0B1930] mb-2 tracking-[-0.01em]">
              Unsere Leistungen
            </h2>
            <p className="text-gray-600">
              Ob Kauf, Verkauf oder Neugründung – wir begleiten Sie bei jedem Schritt.
            </p>
          </div>

          {/* 3 gleichwertige Service-Karten */}
          <div className="grid md:grid-cols-3 gap-5">
            {/* GmbH kaufen */}
            <div className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-[#009E9A] hover:shadow-md transition-all">
              <div className="w-10 h-10 bg-[#E9F7F6] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#009E9A] transition-colors">
                <Building2 className="w-5 h-5 text-[#009E9A] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-bold text-[#0B1930] mb-2">GmbH kaufen</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-5">
                Übernehmen Sie eine bestehende Gesellschaft mit Handelsregistereintragung und starten Sie schneller.
              </p>
              <Link to="/marktplatz">
                <Button variant="outline" className="w-full justify-center border-gray-200 text-[#0B1930] hover:bg-[#E9F7F6] hover:border-[#009E9A] text-sm rounded-lg">
                  Zum Marktplatz
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* GmbH verkaufen - gleichwertig ohne Badge */}
            <div className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-[#009E9A] hover:shadow-md transition-all">
              <div className="w-10 h-10 bg-[#E9F7F6] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#009E9A] transition-colors">
                <TrendingUp className="w-5 h-5 text-[#009E9A] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-bold text-[#0B1930] mb-2">GmbH verkaufen</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-5">
                Diskrete Vermittlung Ihrer Gesellschaft an geprüfte und qualifizierte Interessenten.
              </p>
              <Link to="/seller-onboarding">
                <Button variant="outline" className="w-full justify-center border-gray-200 text-[#0B1930] hover:bg-[#E9F7F6] hover:border-[#009E9A] text-sm rounded-lg">
                  Jetzt anbieten
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* GmbH gründen */}
            <div className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-[#009E9A] hover:shadow-md transition-all">
              <div className="w-10 h-10 bg-[#E9F7F6] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#009E9A] transition-colors">
                <PlusCircle className="w-5 h-5 text-[#009E9A] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-bold text-[#0B1930] mb-2">GmbH gründen</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-5">
                Strukturierte Begleitung von der Vorbereitung bis zur Handelsregistereintragung.
              </p>
              <Link to="/onboarding">
                <Button variant="outline" className="w-full justify-center border-gray-200 text-[#0B1930] hover:bg-[#E9F7F6] hover:border-[#009E9A] text-sm rounded-lg">
                  Formular starten
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Persönliche Unternehmensvermittlung */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-[#F8FAFA]">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Text */}
            <div>
              <div className="inline-flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-[#009E9A]" />
                <span className="text-sm font-semibold text-[#009E9A]">Persönliche Vermittlung</span>
              </div>
              <h2 className="text-2xl md:text-[28px] font-bold text-[#0B1930] mb-4 tracking-[-0.01em]">
                Individuelle Begleitung bei jeder Transaktion
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Neben dem digitalen Marktplatz bieten wir persönliche Beratung und Vermittlung. 
                Wir analysieren Ihre Situation, identifizieren passende Gesellschaften oder Käufer 
                und begleiten Sie strukturiert durch den gesamten Prozess.
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#009E9A] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm text-gray-700">Vertrauliche Behandlung aller Unterlagen</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#009E9A] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm text-gray-700">Persönlicher Ansprechpartner während des gesamten Prozesses</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#009E9A] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm text-gray-700">Koordination mit Notaren, Steuerberatern und weiteren Beteiligten</span>
                </div>
              </div>

              <Link to="/kontakt">
                <Button className="bg-[#0B1930] hover:bg-[#162844] text-white text-sm font-semibold px-6 py-2.5 rounded-lg">
                  Beratungsgespräch vereinbaren
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Kontakt-Box */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
              <h3 className="text-lg font-bold text-[#0B1930] mb-4">Direkter Kontakt</h3>
              <p className="text-sm text-gray-600 mb-6">
                Haben Sie Fragen oder möchten Sie ein unverbindliches Beratungsgespräch? 
                Wir sind persönlich für Sie da.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#E9F7F6] rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#009E9A]" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Telefon</div>
                    <div className="text-sm font-semibold text-[#0B1930]">0170-7967959</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#E9F7F6] rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#009E9A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">E-Mail</div>
                    <div className="text-sm font-semibold text-[#0B1930]">info@firmbay.de</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <Link to="/kontakt">
                  <Button variant="outline" className="w-full justify-center border-[#0B1930] text-[#0B1930] hover:bg-[#E9F7F6] text-sm font-semibold rounded-lg">
                    Kontaktformular öffnen
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#E9F7F6]">
        <div className="max-w-[600px] mx-auto">
          <NewsletterSignup />
        </div>
      </section>
    </div>
  );
};

export default HomeTasteSkillV3;
